import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch, API_BASE } from '~/lib/api';
import { formatDateTime } from '~/lib/date';
import { useT } from '~/i18n';
import AppDownloadBanner from './components/AppDownloadBanner';
import { EmptyState, Icon, PageHeader, Stat, Tone, WaitingInline } from '~/components/ui';
import type { ToneName } from '~/components/ui';
import { fetchCVScore } from './api/cvScoreClient';
import { normalizeScoreDetail, scoreHeadline, WEAK_SCORE, type CVScoreDetail } from './lib/cvScore';
import RecentMessagesCard from './components/RecentMessagesCard';
import DraftDemoCard from './components/DraftDemoCard';
import ApplyCountriesCard from './components/ApplyCountriesCard';
import EnglishAssessmentsSection from './components/EnglishAssessmentsSection';
import type { JobPreferences } from './lib/preferences';
import PaywallModal from './components/PaywallModal';
import PlanOffer from './components/PlanOffer';
import JobsForYouCard from './components/JobsForYouCard';
import JobSearchCard, { goToJobSearch } from './components/JobSearchCard';
import SavedJobsCard from './components/SavedJobsCard';
import ManualApplySheet from './components/ManualApplySheet';
import ConsentStep from './components/ConsentStep';
import { invalidateOnboardingGate } from '~/components/shell/useOnboardingGate';
import MissedWeekCard, { type MissedWeek } from './components/MissedWeekCard';
import PaymentPausedNotice from './components/PaymentPausedNotice';
import GmailReconnectNotice from './components/GmailReconnectNotice';
import {
  fetchJobsForYou,
  fetchSavedJobs,
  setJobSaved,
  type AppliedTodayJob,
  type JobForYou,
  type LockedJobPreview,
  type ManualQuota,
  type SavedJob,
} from './api/freeTierClient';
import { DEMO_LIMIT, demoErrorCopy, sendDemo, type DemoSendResult, type DraftApplication } from './lib/demo';
import { NO_PLAN, PLAN_PRICE, formatEndedPlanDate, formatPlanDate, planLabel, readPlan, type PlanState } from './lib/plan';
import { usePlanCta, type PlanCtaSurface } from './lib/planCta';

interface SenderInfo {
  send_channel: 'gmail' | 'domain';
  sender_address: string | null;
  reply_to: string;
  forward_replies: boolean;
  /** Gmail conectado: la hoja de postular a mano envía desde ahí; sin él, se copia. */
  has_gmail: boolean;
  /** Google rechazó el token: no sale nada hasta reconectar. */
  gmail_reconnect_required: boolean;
}

interface DashboardData {
  membership: {
    email: string;
    name?: string;
    status: string;
    daily_limit: number;
    auto_send: boolean;
    /** Sólo se leen `countries` y `eu_work_authorization` acá (la tarjeta de países). */
    preferences?: JobPreferences | null;
  };
  /** El servidor ya publica vacantes de EURES: la tarjeta de países ofrece Europa. */
  eures_available?: boolean;
  stats: {
    today: number;
    pending: number;
    replied: number;
    interviews: number;
    drafts: number;
    remaining_today: number;
  };
  isWeekend: boolean;
  recentApplications: Array<{
    _id: string;
    email_subject: string;
    match_reason?: string;
    status: string;
    created_at: string;
  }>;
  drafts: DraftApplication[];
  /**
   * `JOB_APPLY_FREE_TIER=on` en el servidor: se postula a mano sin plan, y el
   * panel deja de ser un muro. Ausente o false = el panel de siempre.
   */
  free_tier?: boolean;
  /** «Lo que se te fue»: null con plan activo o con el interruptor apagado. */
  missed_week?: MissedWeek | null;
  /**
   * La hoja de vida con la que se está aplicando y su nota. `score` en null NO
   * es cero: es un CV anterior al puntaje, que se calcula pidiéndolo una vez.
   */
  cv?: {
    _id: string;
    source: string;
    skills: string[];
    created_at: string | null;
    score: number | null;
    score_detail: unknown;
  } | null;
  /**
   * 'method_change' los 14 días después de pasar la hoja de vida a piezas: la
   * nota se recalculó con el método nuevo y pudo moverse sin que la persona
   * cambiara nada. Tiene que saber por qué.
   */
  cv_score_note?: 'method_change' | null;
}

/**
 * Estado → tono de marca. La semántica es la de tbit-canvas y no una paleta
 * elegida por gusto: mint es el dato que volvió (respondieron, hay entrevista),
 * coral es la fricción (lo que espera y lo que se cayó) y el púrpura es lo que
 * está en marcha. Lo que todavía no significa nada va apagado.
 */
const STATUS_TONES: Record<string, ToneName> = {
  DRAFT: 'warn',
  QUEUED: 'info',
  SENT: 'info',
  REPLIED: 'good',
  INTERVIEW: 'good',
  REJECTED: 'bad',
  NO_RESPONSE: 'muted',
};

/**
 * El estado de UNA card de borrador. Vive por id, no en un contador global.
 * El error se guarda como el resultado y no como la frase, para que cambie de
 * idioma si la persona cambia de idioma con el aviso en pantalla.
 */
type DemoCardState = { status: 'idle' | 'sending' | 'sent'; error?: DemoSendResult };

/** El regreso de Stripe. `/panel?checkout=success|cancel` lo pone el backend. */
type CheckoutReturn = 'none' | 'pending' | 'active' | 'slow' | 'cancelled';

/** Desde dónde se abre el modal del plan; viaja al checkout como `cta_surface`. */
type ModalSurface = Extract<PlanCtaSurface, 'paywall' | 'manual_sheet' | 'missed_week' | 'jobs_for_you'>;

export default function JobApplyDashboardPage() {
  // `useT` se suscribe al idioma: al cambiarlo se re-renderiza, y `~/lib/date`
  // y `lib/plan` (que leen el idioma activo al llamarse) lo siguen.
  const t = useT('workspace').dashboard;
  const tp = useT('plan');
  const tc = useT('onboarding').improveCV;
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [plan, setPlan] = useState<PlanState>(NO_PLAN);
  const { case: ctaCase } = usePlanCta(plan);
  const [sender, setSender] = useState<SenderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState<'' | 'failed' | 'connection'>('');
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [demoCards, setDemoCards] = useState<Record<string, DemoCardState>>({});
  const [paywallRevealed, setPaywallRevealed] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallSurface, setPaywallSurface] = useState<ModalSurface>('paywall');
  // «Vacantes para ti» (sólo con free_tier). null = todavía cargando.
  const [jobs, setJobs] = useState<JobForYou[] | null>(null);
  const [jobsQuota, setJobsQuota] = useState<ManualQuota | null>(null);
  const [jobsFailed, setJobsFailed] = useState(false);
  /** Sin plan la recomendación llega bloqueada (solo la vista previa); con plan, lo que el envío mandó hoy. */
  const [jobsLocked, setJobsLocked] = useState(false);
  const [jobsPreview, setJobsPreview] = useState<LockedJobPreview[]>([]);
  const [appliedToday, setAppliedToday] = useState<AppliedTodayJob[]>([]);
  const [appliedIds, setAppliedIds] = useState<ReadonlySet<string>>(() => new Set());
  /** «Guardadas», con o sin plan; la estrella de cada fila se prende con `savedIds`. */
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savedIds = useMemo(() => new Set(savedJobs.map((j) => j._id)), [savedJobs]);
  const [sheetJob, setSheetJob] = useState<JobForYou | null>(null);
  /**
   * Postular pidió los términos (409 `terms_required`): el paso de
   * consentimiento ocupa la página. Aceptarlo vuelve a la hoja de la misma
   * vacante; cerrarlo, al panel.
   */
  const [termsOpen, setTermsOpen] = useState(false);
  const jobsRequested = useRef(false);
  const [checkoutReturn, setCheckoutReturn] = useState<CheckoutReturn>('none');
  const [cvScore, setCvScore] = useState<CVScoreDetail | null>(null);
  const draftGenTriggered = useRef(false);
  /** El cálculo a demanda del puntaje se pide UNA vez por sesión, y nunca dos. */
  const scoreRequested = useRef(false);

  // `quiet`: refrescar sin el cargador de pantalla completa, que desmontaría lo
  // que está a la vista (p. ej. el aviso de "tu suscripción sigue activa").
  const fetchDashboard = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const statusRes = await apiFetch('/job-apply/api/onboarding-status');
      const status = await statusRes.json();
      // Connected means "we can send": own Gmail, or the address TBit manages.
      const connected = status.has_email_channel ?? status.has_gmail;
      if (!connected) {
        navigate('/onboarding', { replace: true });
        return;
      }
      setSender({
        send_channel: status.send_channel === 'domain' ? 'domain' : 'gmail',
        sender_address: status.sender_address ?? null,
        reply_to: status.reply_to,
        forward_replies: status.forward_replies !== false,
        has_gmail: status.has_gmail === true,
        gmail_reconnect_required: status.gmail_reconnect_required === true,
      });
      const dashRes = await apiFetch('/job-apply/api/dashboard');
      const d = await dashRes.json();
      if (d) {
        setData(d);
        const p = readPlan(d);
        setPlan(p);
        // Al recargar después de la tercera demostración el muro sigue en pie:
        // el backend no manda un contador, pero las postulaciones ya enviadas
        // vienen marcadas y eso alcanza para saberlo. Sin dibujar el número.
        const previewed = (d.drafts ?? []).filter(
          (x: DraftApplication) => !!x.demo_previewed_at,
        ).length;
        if (!p.plan_active && previewed >= DEMO_LIMIT) setPaywallRevealed(true);

        /**
         * La nota de la hoja de vida. El panel LEE lo guardado y no llama al
         * modelo — salvo la primera vez de un CV viejo, que llega sin puntaje.
         *
         * Ese caso se resuelve aparte, después de pintar: `GET /api/cv/score`
         * calcula y persiste, así que son 2-4 s UNA vez en la vida de ese CV.
         * Colgar el panel de esa llamada sería pagarla en cada carga; no
         * pedirla nunca dejaría sin coaching justo a quien lleva más tiempo
         * esperando colocarse.
         */
        const stored = normalizeScoreDetail(d.cv?.score_detail);
        if (stored) {
          setCvScore(stored);
        } else if (d.cv && !scoreRequested.current) {
          scoreRequested.current = true;
          fetchCVScore()
            .then((state) => {
              if (state.status === 'ok') setCvScore(state.detail);
            })
            .catch(() => {
              /* El panel no depende de esto: sin nota, no hay aviso y ya. */
            });
        }
      }
      return d as DashboardData;
    } catch {
      navigate('/onboarding', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // El checkout dijo `already_subscribed`: lo pintado está viejo, se vuelve a leer.
  const refreshQuietly = useCallback(() => {
    void fetchDashboard({ quiet: true });
  }, [fetchDashboard]);

  useEffect(() => {
    fetchDashboard().then((d) => {
      // Con free_tier y sin plan no se generan muestras: gastaría modelo para
      // quien no paga. Ahí el panel muestra «Vacantes para ti».
      const freeWithoutPlan = !!d?.free_tier && !readPlan(d).plan_active;
      // Auto-generate drafts if auto_send is off and no drafts yet
      if (d && !freeWithoutPlan && !d.membership.auto_send && d.drafts.length === 0 && !draftGenTriggered.current) {
        draftGenTriggered.current = true;
        triggerDraftGeneration();
      }
    });
  }, [fetchDashboard]);

  // «Vacantes para ti»: una vez, cuando el servidor dice free_tier.
  useEffect(() => {
    if (!data?.free_tier || jobsRequested.current) return;
    jobsRequested.current = true;
    fetchJobsForYou().then((state) => {
      if (state.status === 'ok') {
        setJobs(state.jobs);
        setJobsQuota(state.quota);
        setJobsLocked(state.locked);
        setJobsPreview(state.preview);
        setAppliedToday(state.appliedToday);
      } else {
        setJobsFailed(true);
      }
    });
    fetchSavedJobs().then((state) => {
      if (state.status === 'ok') setSavedJobs(state.jobs);
    });
  }, [data?.free_tier]);

  /** Guardar o quitar, al instante; si el servidor no lo toma, vuelve como estaba. */
  const toggleSave = useCallback(
    async (job: JobForYou) => {
      const wasSaved = savedIds.has(job._id);
      const row: SavedJob = { ...job, applied: (job as Partial<SavedJob>).applied === true };
      const without = (list: SavedJob[]) => list.filter((j) => j._id !== job._id);
      setSaveError(null);
      setSavedJobs((prev) => (wasSaved ? without(prev) : [row, ...without(prev)]));
      const r = await setJobSaved(job._id, !wasSaved);
      if (r.ok) return;
      setSavedJobs((prev) => (wasSaved ? [row, ...without(prev)] : without(prev)));
      setSaveError(r.tooMany ? t.jobsForYou.saved.tooMany : t.jobsForYou.saved.failed);
    },
    [savedIds, t],
  );

  const openPaywall = (surface: ModalSurface) => {
    setPaywallSurface(surface);
    setPaywallOpen(true);
  };

  const markApplied = useCallback((jobId: string) => {
    setAppliedIds((prev) => new Set(prev).add(jobId));
  }, []);

  const markClosed = useCallback((jobId: string) => {
    setJobs((prev) => prev?.map((j) => (j._id === jobId ? { ...j, closed: true } : j)) ?? prev);
    setSavedJobs((prev) => prev.map((j) => (j._id === jobId ? { ...j, closed: true } : j)));
  }, []);

  const closeSheet = useCallback(() => setSheetJob(null), []);

  const countOneMore = useCallback(() => {
    setJobsQuota((prev) => (prev ? { ...prev, used: prev.used + 1 } : prev));
  }, []);

  /**
   * El regreso del checkout. La suscripción no queda activa en el instante en
   * que Stripe redirige: el webhook viaja por su cuenta, así que se pregunta
   * hasta que el plan aparezca de verdad en vez de pintar un "listo" que
   * todavía no es cierto.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // `payment=` es la query del checkout viejo del monolito: sigue viva en
    // correos ya enviados y aterriza en el mismo sitio.
    const raw = params.get('checkout') ?? params.get('payment');
    const outcome = raw === 'success' ? 'success' : raw === 'cancel' || raw === 'cancelled' ? 'cancel' : null;
    if (!outcome) return;

    const cleanUrl = () => window.history.replaceState({}, '', window.location.pathname);

    if (outcome === 'cancel') {
      // Sin drama: no compró, y no pasa nada. Su panel sigue igual.
      setCheckoutReturn('cancelled');
      cleanUrl();
      return;
    }

    setCheckoutReturn('pending');
    const poll = setInterval(async () => {
      try {
        const res = await apiFetch('/job-apply/api/dashboard');
        const d = await res.json();
        const p = readPlan(d);
        if (p.plan_active) {
          setData(d);
          setPlan(p);
          setCheckoutReturn('active');
          setPaywallRevealed(false);
          setPaywallOpen(false);
          clearInterval(poll);
          cleanUrl();
        }
      } catch { /* el siguiente intento */ }
    }, 2000);
    const timeout = setTimeout(() => {
      clearInterval(poll);
      setCheckoutReturn('slow');
      cleanUrl();
    }, 30000);

    return () => { clearInterval(poll); clearTimeout(timeout); };
  }, []);

  const triggerDraftGeneration = async () => {
    setGeneratingDrafts(true);
    try {
      const res = await apiFetch('/job-apply/api/generate-drafts', { method: 'POST' });
      // `already_generating` es un 200: otra pestaña ya está generando las
      // mismas tres. Se relee el panel y NO se reintenta — reintentar era la
      // mitad del cliente en el bug de los seis borradores.
      if (res.ok) {
        await fetchDashboard();
      }
    } catch (err) {
      console.error('Draft generation failed:', err);
    } finally {
      setGeneratingDrafts(false);
    }
  };

  /** Se manda ESA postulación al correo del propio candidato. */
  const handleDemoSend = async (id: string) => {
    setDemoCards((prev) => ({ ...prev, [id]: { status: 'sending' } }));
    const result = await sendDemo(id);

    if (result.kind === 'sent') {
      setDemoCards((prev) => ({ ...prev, [id]: { status: 'sent' } }));
      // Acá se descubre el plan: no como un error, sino como el paso siguiente
      // de algo que acaba de funcionar tres veces delante de él.
      if (result.paywall && !result.plan_active) setPaywallRevealed(true);
      return;
    }

    if (result.kind === 'plan_required') {
      setDemoCards((prev) => ({ ...prev, [id]: { status: 'idle' } }));
      setPaywallRevealed(true);
      openPaywall('paywall');
      return;
    }

    setDemoCards((prev) => ({ ...prev, [id]: { status: 'idle', error: result } }));
  };

  const handleActivateAutoSend = async () => {
    setActivating(true);
    setActivationError('');
    try {
      const res = await apiFetch('/job-apply/api/activate-auto-send', { method: 'POST' });
      if (res.ok) {
        await fetchDashboard();
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { code?: string };
      if (res.status === 402 || body.code === 'plan_required') {
        setPaywallRevealed(true);
        openPaywall('paywall');
        return;
      }
      setActivationError('failed');
    } catch {
      setActivationError('connection');
    } finally {
      setActivating(false);
    }
  };

  if (loading) return <WaitingInline label={t.loading} />;

  const stats = data?.stats ?? { today: 0, pending: 0, replied: 0, interviews: 0, drafts: 0, remaining_today: 0 };
  const drafts = data?.drafts ?? [];
  /**
   * El panel sin muro. Sólo con la palabra del servidor; sin ella, todo lo de
   * abajo se comporta exactamente como antes del interruptor.
   */
  const freeTier = data?.free_tier === true;
  /** Sin plan y con free_tier: ni muestras, ni muros; vacantes de verdad. */
  const freeWithoutPlan = freeTier && !plan.plan_active;
  const showDrafts = !!data && !freeWithoutPlan && !data.membership.auto_send && drafts.length > 0;
  const missedWeek = data?.missed_week ?? null;
  const showMissedWeek =
    freeWithoutPlan && !!missedWeek && (missedWeek.new_postings > 0 || missedWeek.replies > 0);
  const renewsOn = formatPlanDate(plan.renews_at);
  // En la prueba gratis la fecha que importa es la del primer cobro.
  const trialEndsOn = plan.trialing ? formatPlanDate(plan.trial_ends_at ?? plan.renews_at) : '';
  // La fecha del final sólo se nombra si ya pasó: una suscripción cancelada o
  // impaga muere ANTES de su `current_period_end`, y ahí "venció el <futuro>"
  // sería una fecha que el candidato desmiente mirando el calendario.
  const endedOn = formatEndedPlanDate(plan.renews_at);
  /** Tuvo plan y se le venció: eso se dice con su fecha, no con el muro genérico. */
  const showExpiredOffer = !freeTier && !plan.plan_active && !!plan.plan && !!plan.renews_at;
  /**
   * El muro. Se descubre al completar la tercera demostración — y también lo ve
   * quien viene del flujo viejo, donde las tres postulaciones de prueba ya
   * salieron a empleadores y `auto_send` quedó encendido sin plan detrás.
   */
  const trialSpent = !!data && data.membership.auto_send && !plan.plan_active;
  const showDiscoveredPaywall =
    !freeTier && (paywallRevealed || trialSpent) && !plan.plan_active && !showExpiredOffer;
  /**
   * La misma oferta ANTES de la tercera muestra, debajo de las postulaciones.
   * Hasta 2026-10-10 el plan sólo aparecía al mandarse las tres, y quien se
   * quedaba en dos (17 de 31 registros sin plan en dos semanas) nunca supo que
   * había una prueba gratis. Las muestras siguen arriba: primero se ve el
   * producto, y el camino para empezar ya no depende de terminarlas.
   */
  const showEarlyOffer =
    !!data && !freeTier && !plan.plan_active && !showExpiredOffer && !showDiscoveredPaywall;

  if (termsOpen) {
    return (
      <ConsentStep
        freeTier={freeTier}
        onClose={() => {
          setTermsOpen(false);
          setSheetJob(null);
        }}
        onDone={() => {
          // La caché del gate todavía dice que faltan los términos.
          invalidateOnboardingGate();
          setTermsOpen(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        scope="TBit Jobs"
        title={t.title}
        actions={
          <>
            {sender?.send_channel !== 'domain' && (
              <a href={`${API_BASE}/job-apply/auth/start`} className="btn btn-ghost btn-sm gap-2">
                <Icon name="refresh" />
                {t.reconnectGmail}
              </a>
            )}
            <Link to="/onboarding?step=cv" className="btn btn-ghost btn-sm gap-2">
              <Icon name="doc" />
              {t.changeCv}
            </Link>
          </>
        }
      />

      {/* La app móvil: se baja una vez y se cierra para siempre. */}
      <AppDownloadBanner />

      {/* La evaluación de inglés que pidió una empresa: arriba de todo porque
          tiene fecha límite y es lo único del panel que espera una acción suya.
          Sin evaluaciones no pinta nada. */}
      <EnglishAssessmentsSection />

      {/* ── El aviso de la hoja de vida ───────────────────────────────────────
          Una línea, y sólo cuando la nota va por debajo del umbral: el panel es
          la pantalla a la que se vuelve todos los días, así que es el único
          lugar donde alguien que ya terminó el alta se entera de que su
          material va flojo. Motiva y no regaña — dice cuántos datos faltan, no
          que su puntaje es malo — y lleva a un solo sitio: el perfil, donde
          está el detalle y el camino para taparlos. */}
      {cvScore && cvScore.score < WEAK_SCORE && (
        <Link to="/perfil/hoja-de-vida" className="score-nudge rise">
          <span className="score-nudge__art">
            <Icon name="sparkle" size={17} />
          </span>
          <span className="min-w-0">
            <span className="score-nudge__title block">{t.scoreNudge}</span>
            <span className="score-nudge__note block">{scoreHeadline(cvScore)}</span>
          </span>
          <span className="score-nudge__go">
            <Icon name="arrow-right" size={18} />
          </span>
        </Link>
      )}

      {/* La nota se recalculó con el método por piezas (migración). Se ve los
          14 días que el servidor manda `cv_score_note`, al lado del aviso de la
          hoja de vida, y lleva a donde se mejora. */}
      {data?.cv_score_note === 'method_change' && (
        <Link to="/perfil/hoja-de-vida/mejorar" className="alert alert-info text-sm">
          <Icon name="sparkle" size={16} />
          <span>{tc.methodChange}</span>
        </Link>
      )}

      {/* Managed sending address — only for applicants who did not connect Gmail */}
      {sender?.send_channel === 'domain' && (
        <div className="card">
          <div className="card-body py-4">
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5">
                <Icon name="mail" size={20} />
              </span>
              <div className="space-y-1">
                {sender.sender_address ? (
                  <p className="text-sm">
                    {t.sender.from}{' '}
                    <span className="font-semibold break-all">{sender.sender_address}</span>
                  </p>
                ) : (
                  <p className="text-sm">{t.sender.preparing}</p>
                )}
                <p className="text-sm hush">
                  {sender.forward_replies
                    ? <>{t.sender.forwardBefore}<span className="font-medium break-all">{sender.reply_to}</span>{t.sender.forwardAfter}</>
                    : t.sender.forwardOff}
                </p>
                <p className="text-xs hush">{t.sender.noInbox}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── El regreso de Stripe ─────────────────────────────────────────── */}
      {checkoutReturn === 'pending' && (
        <div className="alert alert-info text-sm">
          <span className="loading loading-spinner loading-sm" />
          <span>{t.checkout.pending}</span>
        </div>
      )}
      {checkoutReturn === 'slow' && (
        <div className="alert alert-warning text-sm">
          <span>{t.checkout.slow}</span>
        </div>
      )}
      {checkoutReturn === 'active' && (
        <div className="alert alert-success text-sm">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: 'var(--mint)', boxShadow: '0 0 8px var(--mint)' }}
          />
          <span>
            <strong>{t.checkout.activeStrong}</strong>
            {t.checkout.activeAfter}
          </span>
        </div>
      )}

      {/* ── El plan ──────────────────────────────────────────────────────────
          Mint SÓLO cuando está activo: es el único caso en que la línea afirma
          un hecho comprobado contra Stripe. */}
      {/* Cobro fallido: el envío está en pausa. Reemplaza la línea mint. */}
      <PaymentPausedNotice plan={plan} />
      {/* Gmail desconectado: el envío está en pausa hasta reconectar. */}
      <GmailReconnectNotice required={sender?.gmail_reconnect_required === true} />
      {plan.plan_active && !plan.sending_paused && checkoutReturn !== 'active' && (
        <div className="flex items-center gap-3 px-4 py-3 text-sm alert alert-success">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: 'var(--mint)', boxShadow: '0 0 8px var(--mint)' }}
          />
          {plan.trialing ? (
            <span>
              <strong>{trialEndsOn ? t.plan.trialUntil(trialEndsOn) : t.plan.trialActive}</strong>
              {t.plan.trialThen(PLAN_PRICE)}
              {' '}&middot; {t.plan.dailyLimit(data?.membership.daily_limit ?? 20)}
              {' '}&middot;{' '}
              <Link to="/perfil/plan" className="link font-semibold">
                {t.plan.trialManage}
              </Link>
            </span>
          ) : (
            <span>
              <strong>{planLabel(plan.plan)}</strong>
              {t.plan.activeAfter}
              {renewsOn && t.plan.renews(renewsOn)}
              {' '}&middot; {t.plan.dailyLimit(data?.membership.daily_limit ?? 20)}
            </span>
          )}
        </div>
      )}

      {/* Venció: el plan existió y ya no vale. Con su fecha sólo si ya pasó. */}
      {showExpiredOffer && (
        <PlanOffer
          headline={endedOn ? t.expired.headline(endedOn) : t.expired.headlineNoDate}
          lede={t.expired.lede}
          ctaCase={ctaCase}
          surface="dashboard_expired"
          partnerDiscount={plan.partner_discount}
          onAlreadySubscribed={refreshQuietly}
        />
      )}

      {/* El muro, descubierto tras la tercera postulación que se vio llegar. */}
      {showDiscoveredPaywall && (
        <PlanOffer
          headline={tp.paywall.headline}
          lede={tp.paywall.lede}
          ctaCase={ctaCase}
          surface="paywall"
          partnerDiscount={plan.partner_discount}
          onAlreadySubscribed={refreshQuietly}
        />
      )}

      {/* Generating drafts indicator */}
      {generatingDrafts && (
        <div className="alert text-sm">
          <span className="loading loading-spinner loading-sm" />
          <span>{t.generatingDrafts}</span>
        </div>
      )}

      {/* Weekend indicator */}
      {data?.isWeekend && (
        <div className="alert text-sm">
          <Icon name="clock" />
          <span>{t.weekend}</span>
        </div>
      )}

      {/* ── Vacantes para ti (free_tier) ─────────────────────────────────────
          En el lugar de las muestras: postular de verdad, una por una. También
          con plan activo, descontando del cupo del plan. */}
      {freeTier && (
        <JobsForYouCard
          jobs={jobs}
          quota={jobsQuota}
          failed={jobsFailed}
          hasGmail={sender?.has_gmail === true}
          applied={appliedIds}
          onApply={setSheetJob}
          locked={jobsLocked}
          preview={jobsPreview}
          appliedToday={appliedToday}
          onUnlock={() => openPaywall('jobs_for_you')}
          onSearchSelf={goToJobSearch}
          saved={savedIds}
          onToggleSave={toggleSave}
        />
      )}

      {/* ── Guardadas: las que la persona marcó para después ── */}
      {freeTier && (
        <SavedJobsCard
          jobs={savedJobs}
          hasGmail={sender?.has_gmail === true}
          applied={appliedIds}
          onApply={setSheetJob}
          onToggleSave={toggleSave}
          error={saveError}
        />
      )}

      {/* ── El buscador: todas las vacantes, con o sin plan (términos §8) ── */}
      {freeTier && (
        <JobSearchCard
          hasGmail={sender?.has_gmail === true}
          applied={appliedIds}
          onApply={setSheetJob}
          autoLoad={jobsLocked}
          saved={savedIds}
          onToggleSave={toggleSave}
        />
      )}

      {showMissedWeek && missedWeek && (
        <MissedWeekCard missed={missedWeek} plan={plan} onPlan={() => openPaywall('missed_week')} />
      )}

      {/* ── Las postulaciones listas ─────────────────────────────────────────
          Sin plan: cada una lleva su botón y va al correo del propio candidato.
          Con plan: los botones desaparecen — de acá en adelante salen solas. */}
      {showDrafts && (
        <div className="card shadow-lg rise">
          <div className="card-body">
            <h2 className="card-title text-xl">
              {plan.plan_active ? t.drafts.titleActive : t.drafts.titleDemo}
            </h2>
            <p className="hush text-sm mb-4">
              {plan.plan_active ? t.drafts.noteActive : t.drafts.noteDemo}
            </p>

            <div className="space-y-3">
              {drafts.map((draft) => {
                const card = demoCards[draft._id];
                return (
                  <DraftDemoCard
                    key={draft._id}
                    draft={draft}
                    sent={card?.status === 'sent' || !!draft.demo_previewed_at}
                    sending={card?.status === 'sending'}
                    error={card?.error ? demoErrorCopy(card.error) : undefined}
                    showSend={!plan.plan_active}
                    onSend={() => handleDemoSend(draft._id)}
                  />
                );
              })}
            </div>

            {plan.plan_active && (
              <div className="mt-4 space-y-2">
                <button
                  className="btn btn-primary w-full"
                  onClick={handleActivateAutoSend}
                  disabled={activating}
                >
                  {activating && <span className="loading loading-spinner loading-sm" />}
                  {t.drafts.startSending}
                </button>
                <p className="text-xs hush text-center">{t.drafts.startNote}</p>
                {activationError && (
                  <p className="text-sm text-center" style={{ color: 'var(--coral-ink)' }}>
                    {activationError === 'failed'
                      ? t.drafts.activationFailed
                      : t.drafts.activationConnection}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showEarlyOffer && (
        <PlanOffer
          headline={tp.paywall.early.headline}
          lede={ctaCase === 'trial' ? tp.paywall.early.lede : tp.paywall.early.ledeNoTrial}
          ctaCase={ctaCase}
          surface="dashboard_early"
          partnerDiscount={plan.partner_discount}
          onAlreadySubscribed={refreshQuietly}
        />
      )}

      {/* ── A qué países postulamos ──────────────────────────────────────────
          Debajo de las postulaciones listas y no encima: lo primero que se
          mira es lo que está por salir; esto decide de dónde salen las
          próximas. Vive acá y no en Perfil → Preferencias porque es la única
          preferencia que mueve el envío (ver ApplyCountriesCard). */}
      {data && (
        <ApplyCountriesCard preferences={data.membership.preferences} euresAvailable={data.eures_available === true} />
      )}

      {/* Las cinco cifras. Sólo dos son mint —las respuestas y las entrevistas—
          porque son las únicas que significan que alguien del otro lado
          contestó; el resto es actividad propia. Si todas fueran mint, ninguna
          lo sería. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat title={t.stats.today} value={stats.today} desc={t.stats.remaining(stats.remaining_today)} />
        <Stat title={t.stats.pending} value={stats.pending} />
        <Stat title={t.stats.replied} value={stats.replied} tone="data" />
        <Stat title={t.stats.interviews} value={stats.interviews} tone="data" />
        <Stat title={t.stats.ready} value={stats.drafts} tone="friction" />
      </div>

      {/* La bandeja, arriba del historial de envíos: lo que el otro lado
          contestó importa más que lo que salió de acá. La sección se borra sola
          si el endpoint falla — ver el comentario de RecentMessagesCard. */}
      <RecentMessagesCard />

      <div className="card">
        <div className="card-body">
          <h2 className="card-title">{t.recent.title}</h2>
          {!data?.recentApplications ||
          data.recentApplications.length === 0 ? (
            <EmptyState
              icon="send"
              title={t.recent.emptyTitle}
              note={
                plan.plan_active
                  ? t.recent.emptyNoteAuto
                  : freeTier
                    ? t.freeTier.recentEmptyNote
                    : t.recent.emptyNoteDemo
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{t.recent.subject}</th>
                    <th>{t.recent.status}</th>
                    <th>{t.recent.date}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentApplications.map((app) => (
                    <tr key={app._id}>
                      <td>
                        <div>{app.email_subject}</div>
                        {app.match_reason && (
                          <div className="text-xs text-brand italic mt-0.5">{app.match_reason}</div>
                        )}
                      </td>
                      <td>
                        <Tone tone={STATUS_TONES[app.status] ?? 'muted'}>{app.status}</Tone>
                      </td>
                      <td className="text-xs">
                        {formatDateTime(app.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {sheetJob && (
        <ManualApplySheet
          job={sheetJob}
          quota={jobsQuota ?? { used: 0, limit: 10, plan_active: plan.plan_active }}
          hasGmail={sender?.has_gmail === true}
          plan={plan}
          onClose={closeSheet}
          onApplied={markApplied}
          onClosedJob={markClosed}
          onQuota={setJobsQuota}
          onUsedOne={countOneMore}
          onPlan={() => {
            setSheetJob(null);
            openPaywall('manual_sheet');
          }}
          onTerms={() => setTermsOpen(true)}
        />
      )}

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        ctaCase={ctaCase}
        surface={paywallSurface}
        // Con free_tier no hubo muestras: el titular de «ya viste cómo llegan» no aplica.
        headline={freeTier ? t.freeTier.headline : undefined}
        lede={freeTier ? t.freeTier.lede : undefined}
        partnerDiscount={plan.partner_discount}
        onAlreadySubscribed={refreshQuietly}
      />
    </div>
  );
}
